import request from '@/utils/request';
import { PAGE_SIZE } from '@/domain/market/feed-query';

/**
 * liyuan-market 用户侧接口封装。
 *
 * 暂未对接（见 OpenAPI，按需迭代）：
 * - /sport/events、/sport/competitions、/sportradar/token — 体育赛况走 sportnano + sportsPage/sports/list
 * - /question/chain/biz/{id} — 创建市场上链状态
 * - /market/update — 编辑市场
 * - /strategy/* — 在 liyuan-trade 模块
 */

/** 新增市场集合 POST /liyuan-market/collection/add */
export function addMarketCollection(data) {
  return request.post('/liyuan-market/collection/add', data);
}

/**
 * 分页查询市场集合列表 POST /liyuan-market/collection/page
 * @param {object} query CollectionQuery — category, question, sortType(1=成交量降序), tagId, id, active, closed, featured
 * @param {object} pagination pageNum、pageSize 拼在 URL query
 */
export function getMarketCollectionPage(query = {}, pagination = {}) {
  return request.post('/liyuan-market/collection/page', query, {
    params: {
      pageNum: pagination.pageNum ?? 1,
      pageSize: pagination.pageSize ?? PAGE_SIZE,
    },
  });
}

export function getCountryHomePage(query) {
  return request.post('/liyuan-market/collection/homePage', query);
}

/** 分页查询加密货币市场集合列表 POST /liyuan-market/collection/cryptoPage */
export function getCryptoCollectionPage(query = {}, pagination = {}) {
  return request.post('/liyuan-market/collection/cryptoPage', query, {
    params: {
      pageNum: pagination.pageNum ?? 1,
      pageSize: pagination.pageSize ?? 24,
    },
  });
}

/**
 * 分页查询加密货币市场集合列表（通过 slug 前缀 + 时间区间）
 * POST /liyuan-market/collection/cryptoPageBySlug
 * @param {object} query CryptoSlugCollectionQuery — collSlug, startTime, endTime
 */
export function getCryptoCollectionPageBySlug(query = {}, pagination = {}) {
  return request.post('/liyuan-market/collection/cryptoPageBySlug', query, {
    params: {
      pageNum: pagination.pageNum ?? 1,
      pageSize: pagination.pageSize ?? 20,
    },
  });
}

/**
 * 查询加密货币市场集合（相同 slug 前缀只返回一个）
 * POST /liyuan-market/collection/cryptoPageGroupBySlug
 * @param {object} query CryptoCollectionQuery — cryptoSlug, currencySlug, priceSlug, question
 * @returns {Promise<object[]>} CollectionVo[]
 */
export function getCryptoCollectionPageGroupBySlug(query = {}) {
  return request.post('/liyuan-market/collection/cryptoPageGroupBySlug', query ?? {});
}

/** 分页查询体育市场集合列表 POST /liyuan-market/collection/sportsPage */
export function getSportsCollectionPage(query = {}, pagination = {}) {
  return request.post('/liyuan-market/collection/sportsPage', query, {
    params: {
      pageNum: pagination.pageNum ?? 1,
      pageSize: pagination.pageSize ?? 20,
    },
  });
}

/** 分页查询市场列表并合并用户搜索 GET /liyuan-market/market/page-with-users */
export function getMarketPageWithUsers(keyword, pagination = {}) {
  const q = String(keyword ?? '').trim();
  if (!q) return Promise.reject(new Error('keyword required'));
  return request.get('/liyuan-market/market/page-with-users', {
    keyword: q,
    pageNum: pagination.pageNum ?? 1,
    pageSize: pagination.pageSize ?? 6,
  });
}

/** 查询我创建的市场 POST /liyuan-market/market/my */
export function getMyCreatedMarkets(query = {}, pagination = {}) {
  return request.post('/liyuan-market/market/my', query, {
    params: {
      pageNum: pagination.pageNum ?? 1,
      pageSize: pagination.pageSize ?? 10,
    },
  });
}

/** 分页查询分类列表 GET /liyuan-market/category/page */
export function getMarketCategoryPage(params = {}) {
  return request.get('/liyuan-market/category/page', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 100,
    category: params.category,
    dataSource: params.dataSource,
  });
}

/** 分页查询标签列表 GET /liyuan-market/tag/page */
export function getMarketTagPage(params = {}) {
  return request.get('/liyuan-market/tag/page', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 100,
    label: params.label,
  });
}

/** 根据 ID 查询市场集合 GET /liyuan-market/collection/get/{id} */
export function getMarketCollectionById(id) {
  const cid = String(id ?? '').trim();
  if (!cid) return Promise.reject(new Error('collection id required'));
  return request.get(`/liyuan-market/collection/get/${encodeURIComponent(cid)}`);
}

/**
 * 记录市场集合有效浏览 POST /liyuan-market/collection/view/record
 * 公开接口；服务端负责访客Cookie、30分钟去重以及明细+日汇总事务。
 */
export function recordCollectionView(data) {
  return request.post('/liyuan-market/collection/view/record', data);
}

/** 根据 ID 查询市场 GET /liyuan-market/market/get/{id} */
export function getMarketById(id) {
  const marketId = String(id ?? '').trim();
  if (!marketId) return Promise.reject(new Error('market id required'));
  return request.get(`/liyuan-market/market/get/${encodeURIComponent(marketId)}`);
}

/** 查询全部启用的体育分类列表 GET /liyuan-market/sports/list */
export function getSportsList() {
  return request.get('/liyuan-market/sports/list');
}

/** 查询加密货币筛选项及数量 GET /liyuan-market/crypto/list */
export function getCryptoList() {
  return request.get('/liyuan-market/crypto/list');
}

/** 分页查询币种列表 GET /liyuan-market/currency/page */
export function getCurrencyPage(params = {}) {
  return request.get('/liyuan-market/currency/page', {
    pageNum: params.pageNum ?? 1,
    pageSize: params.pageSize ?? 100,
    currencyName: params.currencyName,
    openStatus: params.openStatus,
  });
}

/** 根据 marketId 查询 token GET /liyuan-market/token-chain/getToken */
export function getToken(marketId) {
  return request.get(`/liyuan-market/token-chain/getToken`, { marketId });
}

/** 查询市场是二元或多元，二元返回2，n元返回n */
export function getOutcomeNum(marketId) {
  return request.get(`/liyuan-market/market/getOutcomeNum/${marketId}`);
}

/** 体育赛事详情：支持 eventId / originId / collId / marketId（后端 flexible 解析）GET /liyuan-market/sport/events/{id} */
export function getSportEventDetail(id) {
  const eventId = String(id ?? '').trim();
  if (!eventId) return Promise.reject(new Error('event id required'));
  return request.get(`/liyuan-market/sport/events/${encodeURIComponent(eventId)}`);
}
